import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * InfoRow / InfoList — the key-value display pervasive on detail pages: a muted
 * label on the left, its value right-aligned. The read-only counterpart to a
 * form field (a `Field` wraps a control; an `InfoRow` shows a settled value).
 *
 *   <InfoList>
 *     <InfoRow label="Stage"><Badge color="blue">Target</Badge></InfoRow>
 *     <InfoRow label="Account Owner">Dana Reyes</InfoRow>
 *     <InfoRow label="1yr Volume Forecast" numeric>$142,000</InfoRow>
 *   </InfoList>
 *
 * The value is `children`, so it can be plain text, a `Badge`, a `Link`, or any
 * node. `numeric` right-sizes figures with `tabular-nums`. `InfoList` stacks
 * rows; `divided` adds hairline rules between them for a denser, tabular feel.
 */

export interface InfoRowProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The field name — muted, left-aligned. */
  label: React.ReactNode
  /** The value — right-aligned; plain text, a Badge, a Link, or any node. */
  children: React.ReactNode
  /** Align figures with `tabular-nums` (IDs, currency, counts). */
  numeric?: boolean
}

export const InfoRow = React.forwardRef<HTMLDivElement, InfoRowProps>(
  ({ label, children, numeric, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-start justify-between gap-4 text-sm', className)}
      {...props}
    >
      <span className="shrink-0 text-fbui-gray-500">{label}</span>
      <span
        className={cn(
          'min-w-0 text-right font-medium text-fbui-gray-900',
          numeric && 'tabular-nums'
        )}
      >
        {children}
      </span>
    </div>
  )
)
InfoRow.displayName = 'InfoRow'

export interface InfoListProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Hairline rules between rows (denser, tabular feel). Default `false`. */
  divided?: boolean
}

/** Stacks {@link InfoRow}s. `divided` separates them with hairline rules. */
export const InfoList = React.forwardRef<HTMLDivElement, InfoListProps>(
  ({ divided, className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col',
        divided
          ? '[&>*]:border-b [&>*]:border-fbui-gray-100 [&>*]:py-2.5 [&>*]:first:pt-0 [&>*]:last:border-0 [&>*]:last:pb-0'
          : 'gap-2.5',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
)
InfoList.displayName = 'InfoList'
